import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import CreateUserWizard from '.';
import {
  ACCOUNT_STEP,
  CREATING_PANEL,
  DETAILS_STEP,
  ERROR_PANEL,
  PERMISSIONS_STEP,
  SUCCESS_PANEL,
} from './copy';

const FORM_OPTIONS = {
  jobRoles: [{ label: 'Fleet Manager', value: 'fleet_manager' }],
  countries: [{ label: 'United States', value: 'US' }],
  states: { US: [{ label: 'Virginia', value: 'VA' }] },
  phoneCountryCodes: [{ label: '+1', value: 1 }],
  permissions: [
    { id: 'fleet-administrator', label: 'Fleet Administrator', roleId: 'r1' },
    { id: 'report-user', label: 'Report User', roleId: 'r2' },
    {
      id: 'management-role',
      label: 'Management Role',
      choices: [
        { label: 'Member', roleId: 'r3' },
        { label: 'Manager', roleId: 'r4' },
      ],
    },
  ],
};

const ACCOUNTS = [
  { label: 'Alpha Fleet', value: 7 },
  { label: 'Beta Fleet', value: 8 },
];

const CREATED = {
  id: 42,
  username: 'jane.doe',
  name: 'Jane Doe',
  editPath: '/users/42/edit',
};

let createResponse;
let availability;

// The wizard talks to four endpoints, so the mock dispatches on path.
const mockFetch = () => {
  global.fetch = jest.fn().mockImplementation((url, options = {}) => {
    if (options.method === 'POST') return Promise.resolve(createResponse);

    if (String(url).startsWith('/management/users/admin_accounts')) {
      return Promise.resolve({
        ok: true,
        json: async () => ({ accounts: ACCOUNTS }),
      });
    }

    if (String(url).startsWith('/management/users/availability')) {
      return Promise.resolve({ ok: true, json: async () => availability });
    }

    return Promise.resolve({ ok: true, json: async () => FORM_OPTIONS });
  });
};

const onClose = jest.fn();
const onCreated = jest.fn();

const renderWizard = () =>
  render(
    <ConfigProvider>
      <CreateUserWizard visible onClose={onClose} onCreated={onCreated} />
    </ConfigProvider>,
  );

const button = (name) => screen.getByRole('button', { name });

const continueButton = () => button(/^continue$/i);

/**
 * Ticks the accounts in the picker and applies them.
 *
 * The options are clicked by their label rather than by role: `getByRole` computes
 * accessibility, which calls getComputedStyle, and jsdom's selector engine throws
 * on KeyDS's sibling-combinator rules ("Cannot read properties of null (reading
 * 'parentElement')"). A jsdom limitation, not a problem with the component — the
 * same query works in a browser.
 */
const chooseAccounts = async (...labels) => {
  fireEvent.click(button(new RegExp(ACCOUNT_STEP.label, 'i')));

  await waitFor(() => expect(button(/^apply$/i)).toBeInTheDocument());

  labels.forEach((label) => fireEvent.click(screen.getByText(label)));
  fireEvent.click(button(/^apply$/i));
};

const fillDetails = () => {
  fireEvent.change(screen.getByLabelText(DETAILS_STEP.firstName), {
    target: { value: 'Jane' },
  });
  fireEvent.change(screen.getByLabelText(DETAILS_STEP.lastName), {
    target: { value: 'Doe' },
  });
  fireEvent.change(screen.getByLabelText(DETAILS_STEP.email), {
    target: { value: 'jane.doe@example.com' },
  });
};

/** Walks from step 1 to step 3 with the given accounts chosen. */
const reachPermissions = async (...accounts) => {
  await chooseAccounts(...(accounts.length ? accounts : ['Alpha Fleet']));
  await waitFor(() => expect(continueButton()).toBeEnabled());
  fireEvent.click(continueButton());

  fillDetails();
  await waitFor(() => expect(continueButton()).toBeEnabled());
  fireEvent.click(continueButton());

  await waitFor(() =>
    expect(
      screen.getByText(PERMISSIONS_STEP.permissionsTitle),
    ).toBeInTheDocument(),
  );
};

beforeEach(() => {
  onClose.mockClear();
  onCreated.mockClear();
  availability = { emailTaken: false, mobileTaken: false, mobileValid: true };
  createResponse = { ok: true, json: async () => ({ user: CREATED }) };
  mockFetch();
});

describe('management/Users CreateUserWizard', () => {
  it('opens on the first step', async () => {
    renderWizard();

    await waitFor(() =>
      expect(screen.getByText(ACCOUNT_STEP.subtitle)).toBeInTheDocument(),
    );
  });

  it('shows every step in the tracker', async () => {
    renderWizard();

    await waitFor(() =>
      expect(screen.getByText(ACCOUNT_STEP.subtitle)).toBeInTheDocument(),
    );

    expect(screen.getByText(DETAILS_STEP.title)).toBeInTheDocument();
    expect(screen.getByText(PERMISSIONS_STEP.title)).toBeInTheDocument();
  });

  // The mockup greys CONTINUE out until the step is satisfied, which for step 1
  // means at least one account.
  it('keeps CONTINUE disabled until an account is chosen', async () => {
    renderWizard();

    await waitFor(() => expect(continueButton()).toBeDisabled());

    await chooseAccounts('Alpha Fleet');

    await waitFor(() => expect(continueButton()).toBeEnabled());
  });

  it('keeps CONTINUE disabled on step 2 until the required fields are filled', async () => {
    renderWizard();

    await chooseAccounts('Alpha Fleet');
    await waitFor(() => expect(continueButton()).toBeEnabled());
    fireEvent.click(continueButton());

    await waitFor(() => expect(continueButton()).toBeDisabled());

    fillDetails();

    await waitFor(() => expect(continueButton()).toBeEnabled());
  });

  // The whole point of the step: a duplicate must not get through.
  it('blocks CONTINUE when the email already belongs to someone', async () => {
    availability = { emailTaken: true, mobileTaken: false, mobileValid: true };
    renderWizard();

    await chooseAccounts('Alpha Fleet');
    await waitFor(() => expect(continueButton()).toBeEnabled());
    fireEvent.click(continueButton());

    fillDetails();
    fireEvent.blur(screen.getByLabelText(DETAILS_STEP.email));

    await waitFor(() =>
      expect(screen.getByText(DETAILS_STEP.emailTaken)).toBeInTheDocument(),
    );
    expect(continueButton()).toBeDisabled();
  });

  describe('the permissions step', () => {
    it('shows the multi-account notice for more than one account', async () => {
      renderWizard();

      await reachPermissions('Alpha Fleet', 'Beta Fleet');

      expect(
        screen.getByText(PERMISSIONS_STEP.multiAccountNotice),
      ).toBeInTheDocument();
    });

    // With one account there is nothing to distinguish, so the notice would only
    // be noise.
    it('omits the notice for a single account', async () => {
      renderWizard();

      await reachPermissions('Alpha Fleet');

      expect(
        screen.queryByText(PERMISSIONS_STEP.multiAccountNotice),
      ).not.toBeInTheDocument();
    });

    it('keeps CREATE USER disabled until a permission is chosen', async () => {
      renderWizard();

      await reachPermissions();

      expect(button(/^create user$/i)).toBeDisabled();

      fireEvent.click(screen.getByLabelText('Fleet Administrator'));

      await waitFor(() => expect(button(/^create user$/i)).toBeEnabled());
    });

    // Regression: KeyDS's Checkbox does not reflect `checked` inside the wizard's
    // portal on React 16, so "keeps CREATE USER disabled..." above passed on state
    // alone while the box looked untouched on screen. See ./wizardInputs.
    it('shows the box as checked once ticked', async () => {
      renderWizard();

      await reachPermissions();

      expect(screen.getByLabelText('Fleet Administrator')).not.toBeChecked();

      fireEvent.click(screen.getByLabelText('Fleet Administrator'));

      await waitFor(() =>
        expect(screen.getByLabelText('Fleet Administrator')).toBeChecked(),
      );
    });

    it('unticks the box again on a second click', async () => {
      renderWizard();

      await reachPermissions();

      fireEvent.click(screen.getByLabelText('Fleet Administrator'));
      await waitFor(() =>
        expect(screen.getByLabelText('Fleet Administrator')).toBeChecked(),
      );

      fireEvent.click(screen.getByLabelText('Fleet Administrator'));

      await waitFor(() =>
        expect(screen.getByLabelText('Fleet Administrator')).not.toBeChecked(),
      );
    });

    // The radios have the same portal fault as the checkboxes.
    it('shows which management role is selected', async () => {
      renderWizard();

      await reachPermissions();
      fireEvent.click(screen.getByLabelText('Management Role'));

      await waitFor(() =>
        expect(screen.getByLabelText('Member')).toBeInTheDocument(),
      );
      expect(screen.getByLabelText('Member')).toBeChecked();

      fireEvent.click(screen.getByLabelText('Manager'));

      await waitFor(() =>
        expect(screen.getByLabelText('Manager')).toBeChecked(),
      );
      expect(screen.getByLabelText('Member')).not.toBeChecked();
    });

    it('shows a ticked discoverability box as checked', async () => {
      renderWizard();

      await reachPermissions();

      fireEvent.click(screen.getByLabelText(PERMISSIONS_STEP.hiddenExternal));

      await waitFor(() =>
        expect(
          screen.getByLabelText(PERMISSIONS_STEP.hiddenExternal),
        ).toBeChecked(),
      );
    });

    // Member and Manager are mutually exclusive, so they only appear once the
    // group is on.
    it('reveals the management roles only once the group is ticked', async () => {
      renderWizard();

      await reachPermissions();

      expect(screen.queryByLabelText('Member')).not.toBeInTheDocument();

      fireEvent.click(screen.getByLabelText('Management Role'));

      await waitFor(() =>
        expect(screen.getByLabelText('Member')).toBeInTheDocument(),
      );
      expect(screen.getByLabelText('Manager')).toBeInTheDocument();
    });
  });

  describe('submitting', () => {
    const submit = async () => {
      await reachPermissions();
      fireEvent.click(screen.getByLabelText('Fleet Administrator'));
      await waitFor(() => expect(button(/^create user$/i)).toBeEnabled());
      fireEvent.click(button(/^create user$/i));
    };

    it('sends what the admin entered', async () => {
      renderWizard();

      await submit();

      await waitFor(() =>
        expect(screen.getByText(SUCCESS_PANEL.title)).toBeInTheDocument(),
      );

      const post = global.fetch.mock.calls.find(
        ([, options]) => options && options.method === 'POST',
      );
      const payload = JSON.parse(post[1].body);

      expect(payload.user.email).toBe('jane.doe@example.com');
      expect(payload.account_ids).toEqual(['7']);
      expect(payload.role_ids).toEqual(['r1']);
    });

    it('names the created user', async () => {
      renderWizard();

      await submit();

      await waitFor(() =>
        expect(screen.getByText(SUCCESS_PANEL.title)).toBeInTheDocument(),
      );

      expect(
        screen.getByText(new RegExp(CREATED.username)),
      ).toBeInTheDocument();
    });

    // VIEW NEW USER is disabled until the tool has its own edit page — see the
    // note in SuccessPanel. The success panel still shows it, greyed.
    it('does not link to the legacy edit page', async () => {
      renderWizard();

      await submit();

      await waitFor(() =>
        expect(screen.getByText(SUCCESS_PANEL.viewUser)).toBeInTheDocument(),
      );

      expect(
        document.querySelector(`[href="${CREATED.editPath}"]`),
      ).not.toBeInTheDocument();
    });

    // Required by the ticket: the admin never sets a password, so they need to
    // know the user is contacted automatically.
    it('says a welcome email goes out', async () => {
      renderWizard();

      await submit();

      await waitFor(() =>
        expect(
          screen.getByText(new RegExp(SUCCESS_PANEL.emailNotice)),
        ).toBeInTheDocument(),
      );
    });

    it('offers a blank wizard again from CREATE NEW USER', async () => {
      renderWizard();

      await submit();
      await waitFor(() =>
        expect(screen.getByText(SUCCESS_PANEL.title)).toBeInTheDocument(),
      );

      fireEvent.click(button(new RegExp(SUCCESS_PANEL.createAnother, 'i')));

      await waitFor(() =>
        expect(screen.getByText(ACCOUNT_STEP.subtitle)).toBeInTheDocument(),
      );
      expect(continueButton()).toBeDisabled();
    });

    // The list has to pick the new user up, and it only does that on a refetch.
    it('tells the page a user was created when it closes', async () => {
      renderWizard();

      await submit();
      await waitFor(() =>
        expect(screen.getByText(SUCCESS_PANEL.title)).toBeInTheDocument(),
      );

      fireEvent.click(button(new RegExp(SUCCESS_PANEL.close, 'i')));

      expect(onCreated).toHaveBeenCalledTimes(1);
      expect(onClose).toHaveBeenCalledTimes(1);
    });

    // The reported bug: CREATE NEW USER clears the created user to start a fresh
    // form, and the refetch used to be gated on that same value — so cancelling
    // from the second form left the list without the user created in the first.
    it('still tells the page when it is cancelled after CREATE NEW USER', async () => {
      renderWizard();

      await submit();
      await waitFor(() =>
        expect(screen.getByText(SUCCESS_PANEL.title)).toBeInTheDocument(),
      );

      fireEvent.click(button(new RegExp(SUCCESS_PANEL.createAnother, 'i')));
      await waitFor(() =>
        expect(screen.getByText(ACCOUNT_STEP.subtitle)).toBeInTheDocument(),
      );

      fireEvent.click(button(/^cancel$/i));

      expect(onCreated).toHaveBeenCalledTimes(1);
      expect(onClose).toHaveBeenCalledTimes(1);
    });

    // Otherwise every later close refetches for a user created and already
    // fetched in an earlier visit.
    it('does not tell the page again on a later close with nothing created', async () => {
      renderWizard();

      await submit();
      await waitFor(() =>
        expect(screen.getByText(SUCCESS_PANEL.title)).toBeInTheDocument(),
      );
      fireEvent.click(button(new RegExp(SUCCESS_PANEL.close, 'i')));
      onCreated.mockClear();

      // The wizard stays mounted across closes, so its state has to be cleared
      // rather than thrown away with the component.
      fireEvent.click(button(/^cancel$/i));

      expect(onCreated).not.toHaveBeenCalled();
    });

    it('shows the creating panel while the request is in flight', async () => {
      let resolveCreate;
      createResponse = new Promise((resolve) => {
        resolveCreate = resolve;
      });
      global.fetch = jest.fn().mockImplementation((url, options = {}) => {
        if (options.method === 'POST') return createResponse;
        if (String(url).startsWith('/management/users/admin_accounts')) {
          return Promise.resolve({
            ok: true,
            json: async () => ({ accounts: ACCOUNTS }),
          });
        }
        if (String(url).startsWith('/management/users/availability')) {
          return Promise.resolve({ ok: true, json: async () => availability });
        }
        return Promise.resolve({ ok: true, json: async () => FORM_OPTIONS });
      });

      renderWizard();

      await submit();

      // The panel heading and the disabled footer button share the same words,
      // so the body copy is what identifies the panel.
      await waitFor(() =>
        expect(screen.getByText(CREATING_PANEL.body)).toBeInTheDocument(),
      );
      expect(button(new RegExp(CREATING_PANEL.action))).toBeDisabled();

      resolveCreate({ ok: true, json: async () => ({ user: CREATED }) });

      await waitFor(() =>
        expect(screen.getByText(SUCCESS_PANEL.title)).toBeInTheDocument(),
      );
    });

    // The reported bug: a phone number the server rejects came back as "an
    // unexpected error" with the useful message thrown away.
    describe('when the server rejects a field', () => {
      beforeEach(() => {
        createResponse = {
          ok: false,
          status: 422,
          json: async () => ({ errors: { mobile: ['must be valid'] } }),
        };
        mockFetch();
      });

      it('returns to the step that owns the field and says what is wrong', async () => {
        renderWizard();

        await submit();

        await waitFor(() =>
          expect(
            screen.getByText(`${DETAILS_STEP.mobile} must be valid`),
          ).toBeInTheDocument(),
        );
        // Back on step 2, not on the generic error panel.
        expect(screen.getByLabelText(DETAILS_STEP.email)).toBeInTheDocument();
        expect(screen.queryByText(ERROR_PANEL.title)).not.toBeInTheDocument();
      });

      it('clears the message once the admin edits the field', async () => {
        renderWizard();

        await submit();
        await waitFor(() =>
          expect(
            screen.getByText(`${DETAILS_STEP.mobile} must be valid`),
          ).toBeInTheDocument(),
        );

        fireEvent.change(screen.getByLabelText(DETAILS_STEP.mobile), {
          target: { value: '2024561111' },
        });

        await waitFor(() =>
          expect(
            screen.queryByText(`${DETAILS_STEP.mobile} must be valid`),
          ).not.toBeInTheDocument(),
        );
      });

      // Nothing was created, so the list must not refetch.
      it('reports nothing created', async () => {
        renderWizard();

        await submit();
        await waitFor(() =>
          expect(
            screen.getByText(`${DETAILS_STEP.mobile} must be valid`),
          ).toBeInTheDocument(),
        );

        expect(onCreated).not.toHaveBeenCalled();
      });
    });

    // A rule the wizard collects nothing for has no field to point at.
    describe('when the server rejects something unmapped', () => {
      beforeEach(() => {
        createResponse = {
          ok: false,
          status: 422,
          json: async () => ({ errors: { passwords: ['are empty'] } }),
        };
        mockFetch();
      });

      it('falls back to the error panel', async () => {
        renderWizard();

        await submit();

        await waitFor(() =>
          expect(screen.getByText(ERROR_PANEL.title)).toBeInTheDocument(),
        );
      });
    });

    describe('when the request fails', () => {
      beforeEach(() => {
        createResponse = { ok: false, status: 500, json: async () => ({}) };
        mockFetch();
      });

      it('shows the error panel', async () => {
        renderWizard();

        await submit();

        await waitFor(() =>
          expect(screen.getByText(ERROR_PANEL.title)).toBeInTheDocument(),
        );
      });

      it('resubmits from RETRY', async () => {
        renderWizard();

        await submit();
        await waitFor(() =>
          expect(screen.getByText(ERROR_PANEL.title)).toBeInTheDocument(),
        );

        const before = global.fetch.mock.calls.filter(
          ([, options]) => options && options.method === 'POST',
        ).length;

        fireEvent.click(button(new RegExp(ERROR_PANEL.retry, 'i')));

        await waitFor(() =>
          expect(
            global.fetch.mock.calls.filter(
              ([, options]) => options && options.method === 'POST',
            ).length,
          ).toBe(before + 1),
        );
      });

      it('offers a way back to the form', async () => {
        renderWizard();

        await submit();
        await waitFor(() =>
          expect(screen.getByText(ERROR_PANEL.title)).toBeInTheDocument(),
        );

        fireEvent.click(button(/^back$/i));

        await waitFor(() =>
          expect(
            screen.getByText(PERMISSIONS_STEP.permissionsTitle),
          ).toBeInTheDocument(),
        );
      });

      // The mockup puts BACK and CLOSE side by side at the right of the footer.
      // Wizard's Footer is row-reverse + space-between, so two *unwrapped* buttons
      // land at opposite ends of it, reversed. jsdom computes no layout, so what is
      // pinned here is the part that is checkable: both buttons share one parent —
      // the FooterActions wrapper that makes the footer treat them as a single item
      // — and sit in the order the mockup reads.
      it('keeps BACK and CLOSE together in the footer', async () => {
        renderWizard();

        await submit();
        await waitFor(() =>
          expect(screen.getByText(ERROR_PANEL.title)).toBeInTheDocument(),
        );

        const back = button(/^back$/i);
        const closeButton = button(new RegExp(`^${ERROR_PANEL.close}$`, 'i'));

        expect(back.parentElement).toBe(closeButton.parentElement);
        expect(
          Array.from(back.parentElement.children).indexOf(back),
        ).toBeLessThan(
          Array.from(closeButton.parentElement.children).indexOf(closeButton),
        );
      });

      // Nothing was created, so the list has no reason to refetch.
      it('does not tell the page anything was created', async () => {
        renderWizard();

        await submit();
        await waitFor(() =>
          expect(screen.getByText(ERROR_PANEL.title)).toBeInTheDocument(),
        );

        fireEvent.click(button(new RegExp(ERROR_PANEL.close, 'i')));

        expect(onCreated).not.toHaveBeenCalled();
        expect(onClose).toHaveBeenCalledTimes(1);
      });
    });
  });
});
